import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import {
  useGetCustomersQuery,
  useGetCustomerQuery,
  useToggleCustomerStatusMutation,
} from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useSelector, useDispatch } from 'react-redux';
import { setFilters } from '../../../store/slices/customersSlice';
import { CustomerFormContent } from './CustomerForm';

function CustomersList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useSelector((state) => state.customers.filters);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '–';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const { data, isLoading, error } = useGetCustomersQuery();
  const [toggleStatus] = useToggleCustomerStatusMutation();
  const { data: editingCustomer, isLoading: loadingCustomer } = useGetCustomerQuery(editingId, {
    skip: !editingId,
  });

  const handleToggleStatus = (id) => {
    toggleStatus(id);
  };

  const customers = Array.isArray(data) ? data : data?.customers ?? [];
  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name?.toLowerCase().includes(search.toLowerCase()) ||
      customer.email?.toLowerCase().includes(search.toLowerCase()) ||
      customer.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleFormSuccess = () => {
    handleCloseDialog();
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading customers</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Customers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{ borderRadius: 2 }}
        >
          Add Customer
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search customers"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => dispatch(setFilters({ search: e.target.value }))}
          placeholder="Search by name, email or code..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Credit Limit (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Current Balance (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers?.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{customer.name}</TableCell>
                      <TableCell>{customer.code || '-'}</TableCell>
                      <TableCell>{customer.email || '-'}</TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>{customer.address || '-'}</TableCell>
                      <TableCell>{formatAmount(customer.creditLimit ?? 0)}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: Number(customer.creditBalance) > 0 ? 'warning.main' : 'text.secondary',
                        }}
                      >
                        {formatAmount(customer.creditBalance ?? 0)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!customer.isActive}
                          onChange={() => handleToggleStatus(customer.id)}
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4ECDC4',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4ECDC4',
                            },
                          }}
                        />
                        <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                          {customer.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/customers/${customer.id}/details`)}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(customer.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No customers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingId ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': {
              overflow: 'visible',
            },
          }}
        >
          <CustomerFormContent
            customer={editingId ? editingCustomer : null}
            isEdit={!!editingId}
            loading={!!editingId && loadingCustomer}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDialog}
            compact
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CustomersList;
